import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Styles from '@pages/analytics/analytics.module.scss';
import DefectsAnalyticTable from '@pages/analytics/common/defectsAnalyticsTable';
import DefectsDonutChart from '@pages/common/charts/defects-donut-chart';
import { chartFontFamily, NO_DATA_FOUND, defectAnalyticsLabel } from '@common/ui-constants';
import { getDefectStatisticTableData, defectsDonutChartCustomTooltip } from '@pages/analytics/common/util';
import { isEmptyValue, scrollTop } from '@src/util/common_utils';
import { colors } from '@src/css_config/colorConstants';

const ModuleStatistics = (props) => {
  let {
    getBugSummary,
    openModuleBugSummaryModal,
    closeBugSummaryModal,
    selectedDefectID,
    moduleChartData,
    reqBody,
    callAllApiOnFilterChange,
  } = props;

  let [series, setSeries] = useState([]);
  let [chartLabel, setChartLabel] = useState([]);
  let [seriesColor, setSeriesColor] = useState([]);
  let [tableData, setTableData] = useState([]);
  let [tableContent, setTableContent] = useState('ALL');
  let [callOnViewAll, setCallOnViewAll] = useState(false);

  //? States for scroll APi :: START
  let [initTable, setInitTable] = useState({
    from: 0,
    to: 20,
  });

  const moduleTableRef = useRef();
  const [moduleTableScrollPos, setModuleTableScrollPos] = useState(false);
  const [stopScrollApiCall, setStopScrollApiCall] = useState(0); //? to stop scroll api on no-content

  let getModuleTableData = async (params = { from: initTable.from, to: initTable.to }) => {
    try {
      let paylodParams = {
        reqBody,
        from: params.from,
        to: params.to,
        tableName: 'Module',
        tableContent: params.tableContent || tableContent,
      };

      if (paylodParams.from === 0 && paylodParams.to === 20) {
        let Response = await getDefectStatisticTableData(paylodParams);
        setTableData(Response.defects);
        setStopScrollApiCall(Response.totalDefects);
        setCallOnViewAll(false); //? to reset viewAll API call state
      } else if (paylodParams.from !== 0 && paylodParams.to !== 20 && stopScrollApiCall > paylodParams.from) {
        let Response = await getDefectStatisticTableData(paylodParams);
        setTableData([...tableData, ...Response.defects]);
        setStopScrollApiCall(Response.totalDefects);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (moduleTableScrollPos) {
      setInitTable({
        from: initTable.to,
        to: initTable.to + 10,
      });
    }
  }, [moduleTableScrollPos]);

  useEffect(() => {
    if (initTable.from !== 0 && initTable.to !== 20) {
      getModuleTableData();
    }
  }, [initTable]);

  //? States for scroll APi :: END

  useEffect(() => {
    setSeries(moduleChartData.series);
    setChartLabel(moduleChartData.label);
    setSeriesColor(moduleChartData.colorCode);
  }, [moduleChartData]);

  // TODO ::NOTE:: when request body change based on side filters

  useEffect(() => {
    if (callAllApiOnFilterChange) {
      setTableContent('ALL');

      let params = {
        from: 0,
        to: 20,
        tableContent: 'ALL',
      };
      getModuleTableData(params);
    }
  }, [callAllApiOnFilterChange]);

  // TODO :: when chick on view All legend reset initTable value & scroll position :: START
  useEffect(() => {
    if (callOnViewAll) {
      setInitTable({
        from: 0,
        to: 20,
      });

      scrollTop(moduleTableRef);

      let params = {
        from: 0,
        to: 20,
      };
      getModuleTableData(params);
    }
  }, [callOnViewAll]);

  // TODO :: when chick on view All legend reset initTable value & scroll position :: END

  let moduleStatisticsColumn = React.useMemo(
    () => [
      {
        Header: 'BUG ID',
        accessor: 'defectId',
        width: '20%',
        Cell: ({ row, value }) => {
          return (
            <div
              className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3 rs-blue cursor-pointer"
              title={value}
              onClick={() => getBugSummary(value, 'moduleBugSummary')}
            >
              {value}
            </div>
          );
        },
      },
      {
        Header: 'SUMMARY',
        accessor: 'summary',
        width: '40%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        Header: 'MODULE',
        accessor: 'content',
        width: '20%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value?.split(':')?.at(0)}>
              {value?.split('/')?.at(-1)?.split(':')?.slice(0, 1)}
            </div>
          );
        },
      },
      {
        Header: 'CREATED BY',
        accessor: 'createdBy',
        width: '20%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
    ],
    []
  );

  let handleClickNameByIndexAndApiCall = (index) => {
    setTableContent(chartLabel.at(index));
    scrollTop(moduleTableRef);

    setStopScrollApiCall(true);

    let params = {
      from: 0,
      to: 20,
      tableContent: chartLabel.at(index),
    };
    getModuleTableData(params);
  };

  let donutOptions = {
    series: [0, 0, 0, 0],
    chart: {
      type: 'donut',
      events: {
        dataPointSelection: function (event, chartContext, config) {
          setInitTable({
            from: 0,
            to: 20,
          });
          if (isEmptyValue(config.selectedDataPoints[0])) {
            //NOTE :: if chart protion selected deselect call All API (Reset)
            setTableContent('ALL');
            scrollTop(moduleTableRef);

            getModuleTableData();
          } else {
            handleClickNameByIndexAndApiCall(config.dataPointIndex);
          }
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
    },
    colors: seriesColor,
    labels: chartLabel,
    plotOptions: {
      bar: {
        borderRadius: 10,
      },
      pie: {
        donut: {
          labels: {
            show: true,
            value: {
              show: true,
              fontSize: '14px',
              fontFamily: chartFontFamily,
              fontWeight: 600,
            },
            total: {
              show: true,
              label: 'Total Defects',
              fontSize: '10px',
              fontFamily: chartFontFamily,
              fontWeight: 600,
              color: colors.black,
            },
            name: {
              show: true,
              fontSize: '22px',
              fontWeight: 600,
              color: undefined,
              offsetY: -10,
              formatter: function (val) {
                if (val?.split('/')?.at(-1).includes(':')) {
                  if (val?.split('/')?.at(-1).split(':').at(0).length > 13) {
                    return val?.split('/')?.at(-1)?.split(':')?.at(0)?.slice(0, 13) + '..';
                  } else {
                    return val?.split('/')?.at(-1)?.split(':')?.at(0);
                  }
                } else if (val?.split('/')?.at(-1).length > 13) {
                  return val?.split('/')?.at(-1).slice(0, 13) + '..';
                } else {
                  return val?.split('/')?.at(-1);
                }
              },
            },
          },
        },
      },
    },
    legend: {
      position: 'right',
      fontSize: 10,
      fontFamily: chartFontFamily,
      fontWeight: 500,
      formatter: (seriesName, options) => {
        let legendName;
        let subModuleName = seriesName?.split('/')?.at(-1);
        let subModuleNameCopy = seriesName?.split('/')?.at(-1);

        if (subModuleName.includes(':')) {
          //NOTE ::  if seriesName have (:) colon trim and take only subModule name
          legendName = subModuleName.split(':').slice(0, 1).toString();
          legendName = legendName.length > 5 ? legendName.slice(0, 5) + '..' : legendName;
        } //NOTE ::  if seriesName don't have (:) colon trim only subModule name
        else {
          legendName = subModuleNameCopy.length > 5 ? subModuleNameCopy.slice(0, 5) + '..' : subModuleNameCopy;
        }

        return `${legendName} : ${options.w.globals.series[options.seriesIndex]}`;
      },
      markers: {
        width: 11,
        height: 8,
        radius: 0,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
    tooltip: {
      enabled: true,
      custom: defectsDonutChartCustomTooltip,
    },
  };

  return (
    <section className={cx(Styles['defectStatisticSection'])}>
      <article className={cx(Styles['leftGarpgBlock'])}>
        <div className="graphHeading ml-2 rs-blue uppercase fontPoppinsSemiboldSm pt-1">
          {' '}
          {defectAnalyticsLabel.label.module}
        </div>
        <DefectsDonutChart
          series={series}
          donutOptions={donutOptions}
          setTableContent={setTableContent}
          setCallOnViewAll={setCallOnViewAll}
          moduleVariable="module"
        />
      </article>
      <article className={cx(Styles['rightTableBlock'])}>
        <DefectsAnalyticTable
          setScrollPos={setModuleTableScrollPos}
          ref={moduleTableRef}
          header={`Defects By Module- ${tableContent.split('/').at(-1)?.split(':').at(0)}`}
          columns={moduleStatisticsColumn}
          data={tableData}
          message={NO_DATA_FOUND}
          openBugSummaryModal={openModuleBugSummaryModal}
          closeBugSummaryModal={closeBugSummaryModal}
          selectedDefectID={selectedDefectID}
        />
      </article>
    </section>
  );
};

export default ModuleStatistics;
